"use client";

import { useState } from "react";
import Script from "next/script";
import { ArrowRight, CheckCircle, AlertCircle, Loader2, Layers, Minus, Plus } from "lucide-react";
import { submitBusinessQuote } from "@/app/business/actions";

declare global {
  interface Window {
    grecaptcha: {
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
      ready: (cb: () => void) => void;
    };
  }
}

const RECAPTCHA_SITE_KEY = (process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? "").trim();
const RECAPTCHA_READY = RECAPTCHA_SITE_KEY.length > 0;

type Status = "idle" | "submitting" | "success" | "error";
type PlanType = "mix-match" | "cloudbreach-plus" | "";

const COURSES = [
  { id: "breaching-azure-plus",          label: "Breaching Azure+",          price: "$1,000", unit: "seat" },
  { id: "breaching-aws-plus",            label: "Breaching AWS+",            price: "$1,000", unit: "seat" },
  { id: "breaching-azure-advanced-plus", label: "Breaching Azure Advanced+", price: "$1,199", unit: "seat" },
  { id: "full-stack-bundle",             label: "Full Stack Bundle (all 3)", price: "$2,650", unit: "seat" },
];

export function BusinessQuoteForm() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [planType, setPlanType]     = useState<PlanType>("");
  const [seatCounts, setSeatCounts] = useState<Record<string, number>>({});
  const [cbpSeats, setCbpSeats]     = useState(1);
  const [courseError, setCourseError] = useState(false);
  const [status, setStatus] = useState<Status>("idle");

  const selectedCourses = Object.keys(seatCounts);

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }

  const fullStackSelected = "full-stack-bundle" in seatCounts;

  function toggleCourse(id: string) {
    setSeatCounts((prev) => {
      if (id in prev) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [id]: _, ...rest } = prev;
        return rest;
      }
      if (id === "full-stack-bundle") {
        // Selecting Full Stack — clear all individual courses
        return { "full-stack-bundle": 1 };
      }
      // Selecting an individual course — clear Full Stack if present
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { "full-stack-bundle": _fs, ...rest } = prev;
      return { ...rest, [id]: 1 };
    });
    setCourseError(false);
  }

  function adjustSeats(id: string, delta: number) {
    setSeatCounts((prev) => ({
      ...prev,
      [id]: Math.max(1, (prev[id] ?? 1) + delta),
    }));
  }

  function setSeatsRaw(id: string, raw: string) {
    const n = parseInt(raw, 10);
    if (!isNaN(n)) setSeatCounts((prev) => ({ ...prev, [id]: Math.max(1, n) }));
  }

  function adjustCbpSeats(delta: number) {
    setCbpSeats((prev) => Math.max(1, prev + delta));
  }

  function setCbpSeatsRaw(raw: string) {
    const n = parseInt(raw, 10);
    if (!isNaN(n)) setCbpSeats(Math.max(1, n));
  }

  function switchPlan(plan: PlanType) {
    setPlanType(plan);
    setSeatCounts({});
    setCourseError(false);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (planType === "mix-match" && selectedCourses.length === 0) {
      setCourseError(true);
      return;
    }

    if (!RECAPTCHA_READY) {
      setStatus("error");
      return;
    }

    setStatus("submitting");

    let planSummary = "";
    if (planType === "mix-match") {
      const lines = COURSES
        .filter((c) => c.id in seatCounts)
        .map((c) => `  - ${c.label}: ${seatCounts[c.id]} seat${seatCounts[c.id] !== 1 ? "s" : ""}`);
      planSummary = `Plan: Mix & Match\nCourses:\n${lines.join("\n")}`;
    } else {
      planSummary = `Plan: CloudBreach+ Subscription\nSeats: ${cbpSeats} seat${cbpSeats !== 1 ? "s" : ""}`;
    }

    try {
      const recaptchaToken = await new Promise<string>((resolve, reject) => {
        window.grecaptcha.ready(async () => {
          try {
            const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: "business_quote" });
            resolve(token);
          } catch (err) {
            reject(err);
          }
        });
      });

      const result = await submitBusinessQuote({
        name:            form.name,
        email:           form.email,
        company:         form.company,
        planSummary,
        message:         form.message,
        locale:          navigator.language,
        timezone:        Intl.DateTimeFormat().resolvedOptions().timeZone,
        recaptchaToken,
      });

      if (!result.ok) throw new Error(result.error);
      setStatus("success");
    } catch {
      setStatus("error");
    }
  }

  if (status === "success") {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-cb-success/10 border border-cb-success/20">
          <CheckCircle className="h-6 w-6 text-cb-success" />
        </div>
        <h3 className="text-base font-semibold text-cb-text">Quote request received</h3>
        <p className="text-sm text-cb-muted max-w-xs leading-relaxed">
          Your account manager will be in touch within one business day.
        </p>
      </div>
    );
  }

  const inputClass =
    "w-full rounded-lg border border-cb-border bg-cb-surface-2 px-4 py-3 text-base text-cb-text placeholder:text-cb-muted-2 outline-none focus:border-cb-violet focus:ring-2 focus:ring-cb-violet/15 transition-colors";

  const labelClass = "block text-xs font-semibold text-cb-muted uppercase tracking-widest mb-2";

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Loaded here instead of the root layout so only pages with this form pay for it */}
      {RECAPTCHA_READY && (
        <Script
          id="recaptcha"
          src={`https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(RECAPTCHA_SITE_KEY)}`}
          strategy="afterInteractive"
        />
      )}
      {!RECAPTCHA_READY && (
        <div className="flex items-start gap-2 rounded-lg border border-cb-danger/20 bg-cb-danger/5 px-4 py-3">
          <AlertCircle className="h-4 w-4 text-cb-danger shrink-0 mt-0.5" />
          <p className="text-sm text-cb-danger leading-relaxed">
            The quote form cannot verify your browser right now. Please try again later or email{" "}
            <a href="mailto:info@cloudbreach.io" className="underline font-medium">
              info@cloudbreach.io
            </a>
            .
          </p>
        </div>
      )}

      {/* Name + Email */}
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="bqf-name" className={labelClass}>
            Name <span className="text-cb-danger normal-case tracking-normal">*</span>
          </label>
          <input
            id="bqf-name" name="name" type="text" required
            placeholder="Jane Smith"
            value={form.name} onChange={handleChange}
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="bqf-email" className={labelClass}>
            Email <span className="text-cb-danger normal-case tracking-normal">*</span>
          </label>
          <input
            id="bqf-email" name="email" type="email" required
            placeholder="jane@yourcompany.com"
            value={form.email} onChange={handleChange}
            className={inputClass}
          />
        </div>
      </div>

      {/* Company */}
      <div>
        <label htmlFor="bqf-company" className={labelClass}>Company</label>
        <input
          id="bqf-company" name="company" type="text"
          placeholder="Your Company Ltd"
          value={form.company} onChange={handleChange}
          className={inputClass}
        />
      </div>

      {/* Plan type */}
      <div>
        <p className={labelClass}>
          Plan type <span className="text-cb-danger normal-case tracking-normal">*</span>
        </p>
        <div role="radiogroup" aria-label="Plan type" className="grid sm:grid-cols-2 gap-3">
          <button
            type="button"
            role="radio"
            aria-checked={planType === "mix-match"}
            onClick={() => switchPlan("mix-match")}
            className={[
              "flex items-start gap-3 rounded-lg border p-4 text-left transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cb-violet/50",
              planType === "mix-match"
                ? "border-cb-violet bg-cb-violet/10"
                : "border-cb-border bg-cb-surface-2 hover:border-cb-violet/40",
            ].join(" ")}
          >
            <div className={[
              "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md border",
              planType === "mix-match" ? "bg-cb-violet/20 border-cb-violet/40" : "bg-cb-surface border-cb-border",
            ].join(" ")}>
              <Layers className={`h-4 w-4 ${planType === "mix-match" ? "text-cb-violet-text" : "text-cb-muted"}`} />
            </div>
            <div>
              <p className={`text-sm font-semibold ${planType === "mix-match" ? "text-cb-text" : "text-cb-muted"}`}>
                Mix &amp; Match
              </p>
              <p className="text-xs text-cb-muted-2 mt-0.5 leading-relaxed">
                Choose the specific courses your team needs
              </p>
            </div>
          </button>

          <button
            type="button"
            role="radio"
            aria-checked={planType === "cloudbreach-plus"}
            onClick={() => switchPlan("cloudbreach-plus")}
            className={[
              "flex items-start gap-3 rounded-lg border p-4 text-left transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cb-violet/50",
              planType === "cloudbreach-plus"
                ? "border-cb-violet bg-cb-violet/10"
                : "border-cb-border bg-cb-surface-2 hover:border-cb-violet/40",
            ].join(" ")}
          >
            <div className={[
              "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border",
              planType === "cloudbreach-plus" ? "bg-cb-violet/20 border-cb-violet/40" : "bg-cb-surface border-cb-border",
            ].join(" ")}
              style={planType === "cloudbreach-plus" ? { boxShadow: "0 0 12px rgba(90,74,227,0.35)" } : undefined}
            >
              <Plus className={`h-4 w-4 ${planType === "cloudbreach-plus" ? "text-cb-violet-text" : "text-cb-muted"}`} />
            </div>
            <div>
              <p className={`text-sm font-semibold ${planType === "cloudbreach-plus" ? "text-cb-text" : "text-cb-muted"}`}>
                CloudBreach+ Subscription
              </p>
              <p className="text-xs text-cb-muted-2 mt-0.5 leading-relaxed">
                All courses, labs &amp; resources included
              </p>
            </div>
          </button>
        </div>
      </div>

      {/* Mix & Match — course + per-course seat selector */}
      {planType === "mix-match" && (
        <div>
          <p className={labelClass}>
            Courses &amp; seats <span className="text-cb-danger normal-case tracking-normal">*</span>
          </p>
          <div className="space-y-2">
            {COURSES.map((course) => {
              const checked = course.id in seatCounts;
              const count   = seatCounts[course.id] ?? 1;
              const isIndividual = course.id !== "full-stack-bundle";
              const disabled = fullStackSelected && isIndividual;
              return (
                <div
                  key={course.id}
                  className={[
                    "rounded-lg border transition-colors",
                    disabled ? "opacity-40" : "",
                    checked ? "border-cb-violet bg-cb-violet/10" : "border-cb-border bg-cb-surface-2",
                  ].join(" ")}
                >
                  {/* Top row — checkbox + label + price */}
                  <label className={`flex items-center justify-between gap-4 px-4 py-3 ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={disabled}
                        onChange={() => toggleCourse(course.id)}
                        className="accent-cb-violet w-4 h-4 shrink-0 disabled:cursor-not-allowed"
                      />
                      <span className={`text-sm font-medium truncate ${checked ? "text-cb-text" : "text-cb-muted"}`}>
                        {course.label}
                      </span>
                    </div>
                    <span className="text-xs text-cb-muted-2 shrink-0">
                      {course.price} / {course.unit}
                    </span>
                  </label>

                  {/* Seat stepper — visible only when checked */}
                  {checked && (
                    <div className="flex items-center gap-3 px-4 pb-3">
                      <span className="text-xs text-cb-muted-2">Seats:</span>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          aria-label="Remove seat"
                          onClick={() => adjustSeats(course.id, -1)}
                          className="flex h-11 w-11 items-center justify-center rounded border border-cb-border bg-cb-surface hover:border-cb-violet/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cb-violet/50"
                        >
                          <Minus className="h-3.5 w-3.5 text-cb-muted" />
                        </button>
                        <input
                          type="number"
                          min={1}
                          value={count}
                          onChange={(e) => setSeatsRaw(course.id, e.target.value)}
                          className="w-14 rounded border border-cb-border bg-cb-surface px-2 py-1 text-center text-base text-cb-text outline-none focus:border-cb-violet focus:ring-1 focus:ring-cb-violet/15 transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        <button
                          type="button"
                          aria-label="Add seat"
                          onClick={() => adjustSeats(course.id, 1)}
                          className="flex h-11 w-11 items-center justify-center rounded border border-cb-border bg-cb-surface hover:border-cb-violet/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cb-violet/50"
                        >
                          <Plus className="h-3.5 w-3.5 text-cb-muted" />
                        </button>
                      </div>
                      <span className="text-xs text-cb-muted-2">
                        seat{count !== 1 ? "s" : ""}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {courseError && (
            <p className="mt-2 text-xs text-cb-danger flex items-center gap-1.5">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              Please select at least one course.
            </p>
          )}
        </div>
      )}

      {/* CloudBreach+ — seat stepper */}
      {planType === "cloudbreach-plus" && (
        <div>
          <p className={labelClass}>Number of seats</p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              aria-label="Remove seat"
              onClick={() => adjustCbpSeats(-1)}
              className="flex h-11 w-11 items-center justify-center rounded-lg border border-cb-border bg-cb-surface-2 hover:border-cb-violet/40 transition-colors shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cb-violet/50"
            >
              <Minus className="h-4 w-4 text-cb-muted" />
            </button>
            <input
              type="number"
              min={1}
              value={cbpSeats}
              onChange={(e) => setCbpSeatsRaw(e.target.value)}
              className="w-20 rounded-lg border border-cb-border bg-cb-surface-2 px-3 py-2.5 text-center text-base text-cb-text outline-none focus:border-cb-violet focus:ring-2 focus:ring-cb-violet/15 transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <button
              type="button"
              aria-label="Add seat"
              onClick={() => adjustCbpSeats(1)}
              className="flex h-11 w-11 items-center justify-center rounded-lg border border-cb-border bg-cb-surface-2 hover:border-cb-violet/40 transition-colors shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cb-violet/50"
            >
              <Plus className="h-4 w-4 text-cb-muted" />
            </button>
            <span className="text-sm text-cb-muted">
              seat{cbpSeats !== 1 ? "s" : ""}
              {cbpSeats >= 3 && <span className="ml-2 text-xs text-cb-violet-text">→ custom pricing applies</span>}
            </span>
          </div>
        </div>
      )}

      {/* Message */}
      <div>
        <label htmlFor="bqf-message" className={labelClass}>Message</label>
        <textarea
          id="bqf-message" name="message" rows={4}
          placeholder="Tell us about your team, goals, or any specific requirements…"
          value={form.message} onChange={handleChange}
          className={`${inputClass} resize-none`}
        />
      </div>

      {status === "error" && (
        <div className="flex items-center gap-2 rounded-lg border border-cb-danger/20 bg-cb-danger/5 px-4 py-3">
          <AlertCircle className="h-4 w-4 text-cb-danger shrink-0" />
          <p className="text-sm text-cb-danger">Something went wrong. Please try again or email us directly.</p>
        </div>
      )}

      <button
        type="submit"
        disabled={status === "submitting" || !planType || !RECAPTCHA_READY}
        className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-cb-violet hover:bg-cb-violet-dim disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold px-6 py-3 transition-colors"
      >
        {status === "submitting" ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Sending…
          </>
        ) : (
          <>
            Request a Quote
            <ArrowRight className="h-4 w-4" />
          </>
        )}
      </button>

      <p className="text-xs text-cb-muted-2 text-center">
        We read every request and reply within one business day.
      </p>
      <p className="text-xs text-cb-muted-2 text-center">
        Protected by reCAPTCHA —{" "}
        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-cb-muted transition-colors">Privacy</a>
        {" & "}
        <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-cb-muted transition-colors">Terms</a>
      </p>
    </form>
  );
}
